import type { FC, ReactNode } from 'react'
import type { LyricsIndexItem, Tag } from '@/types/content'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowUpRight, LoaderCircle, Search, X } from 'lucide-react'
import { cms } from '@/lib/cms'
import { cn } from '@/lib/utils'
import { languageCode } from '@/lib/labels'
import { Select } from '@/components/kit/select'
import { DemoBadge } from '@/components/site/demo-badge'
import { Highlight } from '@/components/site/search-dialog'

interface LyricsLibraryProps {
    songs: Array<LyricsIndexItem>
    genres: Array<Tag>
    moods: Array<Tag>
    initialQuery: string
}

interface Match {
    song_id: string
    language: string
    line: string | null
}

/** Every song with lyrics, searchable by title, phrase and album, and filterable by language, genre and mood. */
export const LyricsLibrary: FC<LyricsLibraryProps> = ({ songs, genres, moods, initialQuery }): ReactNode => {
    const router = useRouter()
    const [query, setQuery] = useState(initialQuery)
    const [language, setLanguage] = useState('')
    const [genre, setGenre] = useState('')
    const [mood, setMood] = useState('')
    const [album, setAlbum] = useState('')
    const [matches, setMatches] = useState<Array<Match> | null>(null)
    const [loading, setLoading] = useState(false)

    const languages = useMemo(() => Array.from(new Set(songs.flatMap(song => song.lyric_languages))).sort(), [songs])
    const albums = useMemo(() => {
        const map = new Map<string, string>()

        songs.forEach(song => {
            if (song.album) map.set(song.album.slug, song.album.title)
        })

        return Array.from(map.entries()).map(([value, label]) => ({ value, label }))
    }, [songs])

    useEffect(() => {
        const term = query.trim()

        if (term.length < 2) {
            setMatches(null)
            setLoading(false)

            return
        }

        const controller = new AbortController()

        setLoading(true)

        const timer = window.setTimeout(async () => {
            try {
                const response = await fetch(`/api/lyrics-search?q=${encodeURIComponent(term)}`, { signal: controller.signal })
                const payload = await response.json()

                if (response.ok && payload.ok) setMatches(payload.data as Array<Match>)
            }
            catch (error) {
                if ((error as Error).name !== 'AbortError') setMatches([])
            }
            finally {
                setLoading(false)
            }
        }, 250)

        const urlTimer = window.setTimeout(() => {
            router.replace({ pathname: '/lyrics', query: term ? { q: term } : {} }, undefined, { shallow: true, scroll: false })
        }, 400)

        return () => {
            controller.abort()
            window.clearTimeout(timer)
            window.clearTimeout(urlTimer)
        }
    }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

    const results = useMemo(() => {
        const matchMap = new Map<string, Match>()

        matches?.forEach(match => {
            if (!matchMap.has(match.song_id) || (language && match.language === language)) matchMap.set(match.song_id, match)
        })

        return songs
            .filter(song => !matches || matchMap.has(song.id))
            .filter(song => !language || song.lyric_languages.includes(language))
            .filter(song => !genre || song.genres.some(item => item.slug === genre))
            .filter(song => !mood || song.moods.some(item => item.slug === mood))
            .filter(song => !album || song.album?.slug === album)
            .map(song => ({ song, match: matchMap.get(song.id) ?? null }))
    }, [songs, matches, language, genre, mood, album])

    const filtered = Boolean(query.trim() || language || genre || mood || album)

    return (
        <div>
            <div className='rounded-2xl border border-white/[0.07] bg-charcoal/50 p-4 sm:p-5'>
                <div className='relative'>
                    <label htmlFor='lyrics-search' className='sr-only'>
                        Search lyrics, titles and albums
                    </label>

                    <Search className='pointer-events-none absolute left-5 top-1/2 size-5 -translate-y-1/2 text-gold' aria-hidden='true' />

                    <input
                        id='lyrics-search'
                        type='search'
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        placeholder='Search a line, a word, a title or an album'
                        className='h-14 w-full rounded-full border border-input bg-obsidian/60 pl-14 pr-12 font-heading text-xl text-foreground placeholder:font-sans placeholder:text-sm placeholder:text-muted-foreground focus:border-gold/60 focus:outline-none'
                    />

                    {loading && <LoaderCircle className='absolute right-5 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground' aria-hidden='true' />}
                </div>

                <div className='mt-3 grid grid-cols-2 gap-2 md:grid-cols-4'>
                    <Select label='Language' value={language} onChange={event => setLanguage(event.target.value)} options={[{ value: '', label: 'All languages' }, ...languages.map(item => ({ value: item, label: item }))]} />
                    <Select label='Album' value={album} onChange={event => setAlbum(event.target.value)} options={[{ value: '', label: 'All albums' }, ...albums]} />
                    <Select label='Genre' value={genre} onChange={event => setGenre(event.target.value)} options={[{ value: '', label: 'All genres' }, ...genres.map(item => ({ value: item.slug, label: item.name }))]} />
                    <Select label='Mood' value={mood} onChange={event => setMood(event.target.value)} options={[{ value: '', label: 'All moods' }, ...moods.map(item => ({ value: item.slug, label: item.name }))]} />
                </div>
            </div>

            <p className='mt-8 text-sm text-muted-foreground' aria-live='polite'>
                {results.length} {results.length === 1 ? 'song' : 'songs'} with lyrics
                {filtered && (
                    <button
                        type='button'
                        onClick={() => {
                            setQuery('')
                            setLanguage('')
                            setGenre('')
                            setMood('')
                            setAlbum('')
                        }}
                        className='ml-3 inline-flex items-center gap-1 text-gold underline-offset-4 hover:underline'
                    >
                        <X className='size-3.5' aria-hidden='true' />
                        Clear
                    </button>
                )}
            </p>

            {results.length === 0 ? (
                <div className='mt-8 rounded-2xl border border-dashed border-white/10 px-6 py-20 text-center'>
                    <p className='font-heading text-3xl'>
                        No lyric holds those words yet.
                    </p>
                    <p className='mx-auto mt-3 max-w-md text-sm text-muted-foreground'>
                        Try a shorter phrase, or search in another language.
                    </p>
                </div>
            ) : (
                <ol className='mt-6 grid gap-px overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.06] md:grid-cols-2'>
                    {results.map(({ song, match }) => {
                        const href = `/lyrics/${song.slug}${match?.language ? `?lang=${encodeURIComponent(match.language)}` : language ? `?lang=${encodeURIComponent(language)}` : ''}`
                        const line = match?.line ?? song.first_line

                        return (
                            <li key={song.id} className='group relative bg-obsidian p-6 transition hover:bg-charcoal sm:p-8'>
                                <div className='flex items-start justify-between gap-4'>
                                    <div className='min-w-0'>
                                        <h3 className='font-heading text-2xl leading-tight' lang={languageCode(song.language)}>
                                            <Link href={href} className='transition-colors after:absolute after:inset-0 group-hover:text-gold'>
                                                <span {...cms('songs', song.id, 'title')}>{song.title}</span>
                                            </Link>
                                        </h3>

                                        {song.album && (
                                            <p className='mt-1 text-xs text-muted-foreground'>
                                                <span {...cms('albums', song.album.id, 'title')}>{song.album.title}</span>
                                            </p>
                                        )}
                                    </div>

                                    <ArrowUpRight className='size-5 shrink-0 text-muted-foreground transition group-hover:text-gold' aria-hidden='true' />
                                </div>

                                {line && (
                                    <p className={cn('mt-5 font-heading text-lg italic leading-snug text-ivory/70')} lang={languageCode(match?.language ?? song.language)}>
                                        &ldquo;<Highlight text={line} term={matches ? query.trim() : ''} />&rdquo;
                                    </p>
                                )}

                                <div className='mt-5 flex flex-wrap items-center gap-2'>
                                    {song.lyric_languages.map(item => (
                                        <span key={item} className='rounded-full border border-ivory/10 px-2.5 py-1 text-[0.65rem] uppercase tracking-[0.14em] text-ivory/55'>
                                            {item}
                                        </span>
                                    ))}

                                    {song.is_demo && <DemoBadge />}
                                </div>
                            </li>
                        )
                    })}
                </ol>
            )}
        </div>
    )
}
